import { useMemo, useState } from "react";
import { Spin, Empty, Modal, Form, Input, message } from "antd";
import {
  useDeleteCooperative,
  useGetCooperativesByUnionId,
  useUpdateCooperative,
} from "../api/address";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { DataTable } from "./ui/data-table";
import { Cooperative } from "../types/cooperative";
import { Icons } from "../assets/images/icons";
import { Button } from "./ui/button";

const CooperativesTable = ({ unionId }: { unionId: number | undefined }) => {
  /** State */
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCooperative, setEditingCooperative] = useState<any>(null);
  const [form] = Form.useForm();

  const { data: cooperativeList, isLoading } = useGetCooperativesByUnionId(
    unionId,
    {
      page: currentPage - 1,
      size: pageSize,
    }
  );

  const { mutate: updateCooperative, isPending: isUpdating } =
    useUpdateCooperative(unionId || 0);
  const { mutate: deleteCooperative } = useDeleteCooperative();

  /** Transform orders for table */
  const transformedData = useMemo((): Cooperative[] => {
    if (!cooperativeList?.content) return [];
    return cooperativeList?.content.map((item: Cooperative) => ({
      id: item.id,
      cooperativeName: item.cooperativeName,
      unionId: item.unionId,
    }));
  }, [cooperativeList]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleEdit = (record: any) => {
    setEditingCooperative(record);
    form.setFieldsValue({ cooperativeName: record.cooperativeName });
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this cooperative?",
      onOk: () => {
        deleteCooperative(id.toString(), {
          onSuccess: () => {
            message.success("Cooperative deleted");
          },
          onError: () => message.error("Delete failed"),
        });
      },
    });
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const cooperative = { cooperativeName: values.cooperativeName };
      updateCooperative(
        { id: editingCooperative.id, cooperative },
        {
          onSuccess: () => {
            message.success("Cooperative updated");
            setIsModalOpen(false);
            form.resetFields();
          },
          onError: () => message.error("Update failed"),
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  /** Columns */
  const cooperativeColumns = [
    {
      key: "cooperativeName",
      title: "Cooperative Name",
      dataIndex: "cooperativeName",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "right" as const,
      render: (_: any, record: Cooperative) => (
        <div className="flex justify-end">
          <Button
            onClick={() => handleEdit(record)}
            variant="secondary"
            className="text-blue-700 hover:text-blue-400 gap-1"
          >
            <EditOutlined className="text-blue-700" />
            Edit
          </Button>
          <Button
            onClick={() => handleDelete(Number(record.id))}
            variant="destructive"
            className="text-red-700 hover:text-red-400 gap-1"
          >
            <DeleteOutlined className="text-red-700" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  if (!unionId) {
    return <Empty description="Please select a union to view cooperatives" />;
  }

  return (
    <>
      {isLoading ? (
        <div className="text-center mt-8">
          <Spin />
        </div>
      ) : (
        <>
          <DataTable<Cooperative>
            data={transformedData}
            columns={cooperativeColumns}
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: cooperativeList?.totalPages ?? 0,
              onChange: handlePaginationChange,
            }}
            emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
            emptyText="No cooperatives found"
            rowKey="id"
          />
        </>
      )}

      {/* Edit Cooperative Modal */}
      <Modal
        title="Edit Cooperative"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleEditSubmit}
        confirmLoading={isUpdating}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Cooperative Name"
            name="cooperativeName"
            rules={[
              { required: true, message: "Please enter cooperative name" },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CooperativesTable;
